/** Sentinel folder id for the "Unassigned" group (options without a folder). */ export const UNASSIGNED_FOLDER_ID = '__unassigned__';
/**
 * Groups a list of select options under folder headings.
 *
 * - If no option carries both a `folderId` and a `folderName`, the original
 *   flat list is returned unchanged (so the select renders as a normal
 *   dropdown).
 * - Otherwise react-select group objects (`{ folderId, label, options }`) are
 *   returned, one per folder (heading = `folderName`). Options without a folder
 *   are collected under a trailing `unassignedLabel` group.
 *
 * Generic over the option shape, so it works for any template-type listing
 * (project templates, customer portals, forms, documents, …) as long as each
 * option carries its own `folderId` / `folderName`.
 */ export const groupOptionsByFolder = (options, unassignedLabel)=>{
    const hasFolderAssignment = options.some((option)=>option.folderId != null && !!option.folderName);
    if (!hasFolderAssignment) {
        return options;
    }
    const groupsById = new Map();
    const unassigned = [];
    options.forEach((option)=>{
        if (option.folderId == null || !option.folderName) {
            unassigned.push(option);
            return;
        }
        const key = String(option.folderId);
        const existingGroup = groupsById.get(key);
        if (existingGroup) {
            existingGroup.options.push(option);
        } else {
            groupsById.set(key, {
                folderId: option.folderId,
                label: option.folderName,
                options: [
                    option
                ]
            });
        }
    });
    const grouped = Array.from(groupsById.values());
    if (unassigned.length > 0) {
        grouped.push({
            folderId: UNASSIGNED_FOLDER_ID,
            label: unassignedLabel,
            options: unassigned
        });
    }
    return grouped;
};
/**
 * Type guard for the result of {@link groupOptionsByFolder}: returns `true`
 * when the options were grouped under folder headings (i.e. each entry is a
 * `{ folderId, label, options }` group) rather than a flat option list. Use it
 * to decide whether folder-aware grouping UI should be rendered.
 */ export const isFolderGrouped = (result)=>result.length > 0 && 'options' in result[0];
